import React, {useState} from 'react';
import {
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	VStack,
} from '@tempo/core';
import PasswordStrengthBar from '@components/PasswordStrengthBar';
import passwordValidator, {
	PasswordLevel,
} from '@deezer/next/shared/utils/password/validator';
import {useTranslation} from 'next-i18next';
import type {UserForm} from '@deezer/next/features/Account/type';

type UserPasswordFormProps = {
	data: string;
	isDisabled: boolean;
	isRequired: boolean;
	isCurrentUser: boolean;
	onChange: (value: Partial<UserForm>, isValid?: boolean) => void;
	onPasswordConfirmationChange?: (isValid: boolean) => void;
};

const UserPasswordForm = ({
	data,
	isDisabled,
	isRequired,
	isCurrentUser,
	onChange,
	onPasswordConfirmationChange,
}: UserPasswordFormProps) => {
	const {t} = useTranslation('d4c');
	const [password, setPassword] = useState(data);
	const [currentPassword, setCurrentPassword] = useState('');
	const [confirmation, setConfirmation] = useState('');
	const [score, setScore] = useState<number>(0);
	const [level, setLevel] = useState<PasswordLevel>(PasswordLevel.WEAK);
	const [warning, setWarning] = useState('');
	const [hasConfirmError, setHasConfirmError] = useState(false);

	const checkPasswordSecurity = (pwd: string) => {
		const passwordValidation = passwordValidator(pwd);
		setScore(passwordValidation.score);
		setLevel(passwordValidation.level);
		setWarning(passwordValidation.getWarningMessage(t));
	};

	const isPasswordValid = (
		currentPassword: string,
		newPassword: string,
		confirmationPassword: string,
	) => {
		const passwordValidation = passwordValidator(newPassword);

		// if the password is required the current password is not required
		if (isRequired) {
			return (
				Boolean(newPassword) &&
				newPassword === confirmationPassword &&
				!passwordValidation.warning
			);
		}

		return (
			!newPassword ||
			(Boolean(currentPassword) &&
				newPassword === confirmationPassword &&
				!passwordValidation.warning)
		);
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		setPassword(value);
		setConfirmation('');

		if (value) {
			checkPasswordSecurity(value);
			setHasConfirmError(true);
		} else {
			setScore(0);
			setLevel(PasswordLevel.WEAK);
			setWarning('');
			setHasConfirmError(false);
		}

		onChange({password: value}, isPasswordValid(currentPassword, value, ''));
	};

	const handlePasswordConfirmationChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const value = e.target.value;
		const isEqual = password === value;
		setConfirmation(value);
		setHasConfirmError(!isEqual);
		onChange({confirmationPassword: value});
		if (onPasswordConfirmationChange) {
			onPasswordConfirmationChange(
				isPasswordValid(currentPassword, password, value),
			);
		}
	};

	const handleCurrentPasswordChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const value = e.target.value;
		setCurrentPassword(value);
		onChange(
			{currentPassword: value},
			isPasswordValid(value, password, confirmation),
		);
	};

	return (
		<VStack spacing={6} width="100%">
			{!isRequired && isCurrentUser && (
				<FormControl
					isRequired={(isRequired || Boolean(password)) && !isDisabled}
				>
					<FormLabel>
						{t('profilesettings_text_currentpassword_d4c-web')}
					</FormLabel>
					<Input
						data-testid={'user-manager-form-current-password'}
						name="password"
						placeholder="*********"
						type="password"
						onChange={handleCurrentPasswordChange}
						value={currentPassword}
					/>
				</FormControl>
			)}

			<FormControl
				isRequired={(isRequired || Boolean(password)) && !isDisabled}
				isInvalid={Boolean(warning)}
			>
				<FormLabel>{t('usermanager_text_newpassword_d4c-web')}</FormLabel>
				<Input
					data-testid={'user-manager-form-new-password'}
					name="password"
					placeholder="*********"
					type="password"
					onChange={handlePasswordChange}
					value={password}
				/>
				<PasswordStrengthBar level={level} score={score} />
				<FormErrorMessage
					color={level === PasswordLevel.MEDIUM ? 'orange' : undefined}
				>
					{warning}
				</FormErrorMessage>
			</FormControl>

			<FormControl
				isRequired={isRequired || Boolean(password)}
				isInvalid={hasConfirmError}
			>
				<FormLabel>
					{t('usermanager_text_confirmnewpassword_d4c-web')}
				</FormLabel>
				<Input
					data-testid={'user-manager-form-confirm-password'}
					name="confirmation"
					placeholder="*********"
					type="password"
					onChange={handlePasswordConfirmationChange}
					value={confirmation}
				/>
				<FormErrorMessage>
					{hasConfirmError &&
						t('toastmessage_text_pleaseenterthesamepassword_d4c-web')}
				</FormErrorMessage>
			</FormControl>
		</VStack>
	);
};

export default UserPasswordForm;
